import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import Heading from '../../atoms/Typography/Heading';
import Text from '../../atoms/Typography/Text';
import Badge from '../../atoms/Badge/Badge';

const isExternalLink = (href) => /^https?:\/\//i.test(href || '');

const ActionCard = ({
  title,
  description,
  icon,
  iconAlt,
  link,
  href,
  disabled = false,
  label,
  bgColor = 'bg-white',
  iconColor = 'text-primary-400',
  className = '',
  onClick,
  ...props
}) => {
  const targetLink = href || link;
  const cardClasses = [
    'relative border border-neutral-200 text-center p-4 sm:p-6 rounded-lg transition-all duration-200',
    bgColor,
    disabled
      ? 'opacity-60 cursor-not-allowed'
      : 'hover:shadow-md hover:cursor-pointer hover:border-primary-200',
    className
  ].filter(Boolean).join(' ');

  const handleClick = (event) => {
    if (disabled) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };

  return (
    <div className={cardClasses} onClick={handleClick} {...props}>
      <div className="flex flex-col gap-x-6 items-center px-4 sm:px-8 py-4 sm:py-5">
        <div className="mb-3 sm:mb-4">
          <img
            src={icon}
            alt={iconAlt || title}
            className={`h-32 w-32 sm:h-22 sm:w-22 ${iconColor}`}
          />
        </div>

        <div className="w-full">
          <Heading level={2} className="text-lg sm:text-2xl font-medium mt-2 sm:mt-3">
            {title}
          </Heading>

          <Text className="mt-2 text-sm sm:text-base leading-6 sm:leading-7 text-gray-800 opacity-80">
            {description}
          </Text>

          {label && (
            <div className="mt-3">
              <Badge variant="default" size="md">
                {label}
              </Badge>
            </div>
          )}
        </div>
      </div>

      {targetLink && !disabled && (
        isExternalLink(targetLink) ? (
          <a
            href={targetLink}
            className="absolute inset-0 z-10"
            aria-label={`Navigate to ${title}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="sr-only">{title}</span>
          </a>
        ) : (
          <Link to={targetLink} className="absolute inset-0 z-10" aria-label={`Navigate to ${title}`}>
            <span className="sr-only">{title}</span>
          </Link>
        )
      )}
    </div>
  );
};

ActionCard.displayName = 'ActionCard';

ActionCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  iconAlt: PropTypes.string,
  link: PropTypes.string,
  href: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  bgColor: PropTypes.string,
  iconColor: PropTypes.string,
  className: PropTypes.string,
  onClick: PropTypes.func
};

export default ActionCard;
